import "./LoginPage.scss";

import { Button, DropdownButton } from "@appkit4/react-components";
import { useState } from "react";
import { Navigate } from "react-router-dom";

import pwcLogo from "../../assets/login/logo.svg";
import loginBackground from "../../assets/login/svg-risk-management-1.svg";
import { isAuthConfigured } from "../../config/authConfig";
import {
  clearPendingAuthError,
  getPendingAuthError,
} from "../../config/msalInstance";
import { useAuth } from "../../contexts/useAuth";
import { legalLinkItems } from "../../types/config/navConfig";
import { AppRoutes } from "../../types/enums/appRoutes";
import {
  FooterCopy,
  FooterUrl,
  LoginLabel,
} from "../../types/enums/labels";

const languageItems = [{ label: LoginLabel.Language, value: "en" }];

/**
 * Full-screen sign-in screen following the AppKit "Login (default)" pattern:
 * a left panel with the brand, welcome copy and sign-in actions, and a right
 * panel showing the product illustration. Renders outside the app shell.
 * Offers Entra SSO (redirect flow) plus a placeholder "Login" button reserved
 * for a future local sign-in flow. Authentication only — no roles.
 */
function LoginPage() {
  const { isAuthenticated, isLoading, login } = useAuth();
  const [authError] = useState<string | null>(() => {
    const err = getPendingAuthError();
    clearPendingAuthError();
    return err;
  });

  const year = new Date().getFullYear();

  // Avoid a flash of the login UI while a redirect is being resolved.
  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to={AppRoutes.home} replace />;

  return (
    <div className="login-default ap-pattern-login">
      <div className="login-default__left">
        <img
          className="login-default__logo"
          src={pwcLogo}
          alt={LoginLabel.PwcLogoAlt}
        />

        <div className="login-default__form">
          <div className="login-default__form-header">
            <div className="login-default__form-heading">
              <span className="login-default__welcome">
                {LoginLabel.WelcomeTo}
              </span>
              <h1 className="login-default__product">
                {LoginLabel.ProductName}
              </h1>
            </div>
            <DropdownButton
              compact
              kind="text"
              splitButton={false}
              data={languageItems}
              prefixTemplate={() => (
                <span
                  className="Appkit4-icon icon-globe-outline"
                  aria-hidden="true"
                />
              )}
            >
              {LoginLabel.Language}
            </DropdownButton>
          </div>

          {authError && (
            <div className="login-default__error" role="alert">
              <strong>{LoginLabel.SignInFailed}</strong> {authError}
            </div>
          )}
          {!isAuthConfigured && (
            <div className="login-default__error" role="alert">
              {LoginLabel.AuthNotConfigured}
            </div>
          )}

          <div className="login-default__actions">
            <Button
              kind="primary"
              className="login-default__button"
              disabled={!isAuthConfigured}
              onClick={login}
            >
              {LoginLabel.SsoLogin}
            </Button>
            {/* Local credential sign-in is reserved for a later implementation. */}
            <Button
              kind="secondary"
              className="login-default__button"
              disabled
              title={LoginLabel.Login}
            >
              {LoginLabel.Login}
            </Button>
          </div>
        </div>

        <footer className="login-default__footer" role="contentinfo">
          <p className="login-default__copy">
            ©{year} {FooterCopy.Copyright} {FooterCopy.LegalEntity}{" "}
            {FooterCopy.StructurePrompt}{" "}
            <a
              href={FooterUrl.PwcStructure}
              target="_blank"
              rel="noopener noreferrer"
              className="login-default__structure-link"
            >
              {FooterCopy.StructureLinkText}
            </a>{" "}
            {FooterCopy.StructureSuffix}
          </p>
          <ul className="login-default__links">
            {legalLinkItems.map(({ to, label }) => (
              <li key={label}>
                <a href={to} className="login-default__link">
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </footer>
      </div>

      <div className="login-default__right">
        <div
          className="login-default__illustration"
          style={{ backgroundImage: `url(${loginBackground})` }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

export default LoginPage;
